import { useCallback, useEffect } from 'react'
import { useFormikContext } from 'formik'

import { CryptoPaymentDropdown } from '../CryptoPaymentDropdown'
import { config } from '../../Init/Init'
import l10n from '../../i18n/I18N'
import Dropdown from '../../Dropdown/Dropdown'
import { usePlayerStore } from '../../../services/store'
import { CryptoPaymentInput } from '../CryptoPaymentFormElements/CryptoPaymentInput'
import { CryptoPaymentFormGroup } from '../CryptoPaymentFormElements/CryptoPaymentFormGroup'
import { formatCurrencyAmount, getCurrencyConfigForWalletId, tokenizer } from '../../../services/utils'
import {
  withdrawFormFields,
  withdrawalSteps,
  formFieldPlaceholders,
  withdrawalDraftDefaultData
} from '../cryptoPaymentConstants'
import { inputActionType } from '../CryptoPaymentFormElements/cryptoPaymentInputConstants'
import { useModifyDraftWithdrawalData } from './hooks'
import { CryptoPaymentWithdrawalFormTimer } from './CryptoPaymentWithdrawalFormTimer'
import { useCryptoPaymentDataModal } from '../CryptoPaymentModalDataProvider'

import classes from './styles/cryptoPaymentWithdrawalForm.module.scss'

import {
  CryptoPaymentWithdrawalFormInfoBottom,
  CryptoPaymentWithdrawalFormInfoTop
} from './CryptoPaymentWithdrawalFormInfo'
import { CryptoPaymentWithdrawalFormButton } from './CryptoPaymentWithdrawalFormButton'

export const CryptoPaymentWithdrawalFromContent = () => {
  const playerStore = usePlayerStore()
  const playerWallets = playerStore.use.playerWallets()

  const {
    loading,
    setLoading,
    withdrawalStep,
    isXRPCryptoType,
    cryptoCurrencies,
    selectedCurrency,
    setWithdrawalStep,
    setSelectedCurrency,
    withdrawalDraftData,
    withdrawalFormError,
    updateDraftWithdrawal,
    setWithdrawalDraftData,
    lastPurchasedCryptoCurrencyData,
    isGetLastPurchasedCryptoCurrencyLoading
  } = useCryptoPaymentDataModal()

  const { setFieldValue, values } = useFormikContext()

  useEffect(() => {
    setLoading(false)
  }, [])

  const currencyConfig = getCurrencyConfigForWalletId(playerWallets, config.swtWalletId)

  const { secondsToUpdateDraftData } = useModifyDraftWithdrawalData({
    withdrawalStep,
    withdrawalDraftData,
    updateDraftWithdrawal
  })

  const onCurrencyChange = useCallback(
    currency => {
      if (values[withdrawFormFields.address]) {
        setFieldValue(withdrawFormFields.address, '')
      }

      if (values[withdrawFormFields.protocol]) {
        setFieldValue(withdrawFormFields.protocol, '')
      }

      setSelectedCurrency(currency)
    },
    [setFieldValue, setSelectedCurrency, values]
  )

  useEffect(() => {
    if (withdrawalStep === withdrawalSteps.submit) {
      setWithdrawalStep(withdrawalSteps.create)
      setWithdrawalDraftData(withdrawalDraftDefaultData)
    }
  }, [values.amountInTargetCurrency, values.currency])

  useEffect(() => {
    // set targetCurrency on init
    setFieldValue(withdrawFormFields.targetCurrency, currencyConfig.currencyId)

    // set currency (crypto) on init
    if (cryptoCurrencies.length !== 0) {
      if (
        lastPurchasedCryptoCurrencyData.completedPurchasesExist &&
        values.currency !== lastPurchasedCryptoCurrencyData.cryptoCurrency
      ) {
        const cryptoCurrency = cryptoCurrencies.find(
          cryptoCurrency => cryptoCurrency.currencyCode === lastPurchasedCryptoCurrencyData.cryptoCurrency
        )

        setFieldValue(withdrawFormFields.currency, cryptoCurrency.currencyCode)
        onCurrencyChange(cryptoCurrency)
      } else if (!values[withdrawFormFields.currency]) {
        setFieldValue(withdrawFormFields.currency, cryptoCurrencies[0].currencyCode)
      }
    }

    // set protocol (if exist) on init
    if (selectedCurrency.protocols.length !== 0 && !values[withdrawFormFields.protocol]) {
      setFieldValue(withdrawFormFields.protocol, selectedCurrency.protocols[0])
    }

    // reset protocol (if not exist)
    if (selectedCurrency.protocols.length === 0) {
      setFieldValue(withdrawFormFields.protocol, null)
    }

    // set any withdrawalDraftData.paidCurrency.amount (crypto to redeem)
    setFieldValue(withdrawFormFields.cryptoAmount, withdrawalDraftData.paidCurrency.amount || '')
  }, [
    setFieldValue,
    withdrawalStep,
    cryptoCurrencies,
    currencyConfig.currencyId,
    selectedCurrency.protocols,
    lastPurchasedCryptoCurrencyData,
    withdrawalDraftData.paidCurrency.amount
  ])

  return (
    <>
      <CryptoPaymentFormGroup>
        <CryptoPaymentFormGroup>
          <CryptoPaymentWithdrawalFormInfoTop playerWallets={playerWallets} currencyConfig={currencyConfig} />
          <CryptoPaymentInput
            disabled={loading}
            afterInput={currencyConfig.currencyName}
            placeholder={formFieldPlaceholders.numeric}
            name={withdrawFormFields.amountInTargetCurrency}
            label={l10n.t['cryptopayment.withdraw.input.amount.label']}
            format={value => formatCurrencyAmount(value, 'GC')}
          />
        </CryptoPaymentFormGroup>
        <CryptoPaymentFormGroup>
          <CryptoPaymentDropdown
            disabled={
              loading ||
              isGetLastPurchasedCryptoCurrencyLoading ||
              lastPurchasedCryptoCurrencyData.completedPurchasesExist
            }
            className="formElement"
            items={cryptoCurrencies}
            onChange={onCurrencyChange}
            name={withdrawFormFields.currency}
          />
          {selectedCurrency.protocols.length !== 0 && (
            <Dropdown
              disabled={loading}
              className="formElement"
              items={selectedCurrency.protocols}
              selectedItem={values[withdrawFormFields.protocol]}
              onChange={protocol => setFieldValue(withdrawFormFields.protocol, protocol)}
            />
          )}
          {isXRPCryptoType && (
            <CryptoPaymentInput
              name={withdrawFormFields.destinationTag}
              label={l10n.t['cryptopayment.withdraw.tag.label']}
            />
          )}
          <CryptoPaymentInput
            disabled={loading}
            actionType={config.showCryptoWalletPasteButton && inputActionType.paste}
            name={withdrawFormFields.address}
            label={tokenizer(l10n.t['cryptopayment.deposit.address.label'], {
              '{currency}': selectedCurrency.currencyCode
            })}
          />
          <CryptoPaymentInput
            name={withdrawFormFields.cryptoAmount}
            afterInput={selectedCurrency.currencyCode}
            placeholder={formFieldPlaceholders.crypto}
            readOnly
            type="number"
            label={l10n.t['cryptopayment.redeem.receiveAmount.label']}
          />
        </CryptoPaymentFormGroup>
      </CryptoPaymentFormGroup>
      <CryptoPaymentWithdrawalFormInfoBottom currencyConfig={currencyConfig} />
      {withdrawalFormError && !loading && (
        <div className={classes.cryptoPaymentWithdrawalForm__error}>{withdrawalFormError}</div>
      )}
      <CryptoPaymentWithdrawalFormButton />
      {withdrawalStep === withdrawalSteps.submit && !withdrawalFormError && (
        <CryptoPaymentWithdrawalFormTimer secondsToUpdateDraftData={secondsToUpdateDraftData} />
      )}
    </>
  )
}
